import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Link, SelectInput } from '@ktag/styleguide/dist/react';
import { Button as ButtonAV, MetaLayerDefinition } from '@agissc/ktag-map';
import { useMetadataQuery } from '../../api/useMetadataQuery';
import useAppState from '../../../../AppStore';

import useSubLayersSetting from '../../api/useSubLayersSetting';
import SearchValueInput from './SearchValueInput';
import { useTracking } from '../../../../utils/tracking/TrackingContext';
import MessageBox from './MessageBox';

interface TopicLayerAttributeSearchQueryFieldsProps {
  topicLayer: MetaLayerDefinition;
  showInitialMessage: boolean;
  onSearchRequest: () => void;
}

const TopicLayerAttributeSearchQueryFields = ({
  topicLayer,
  showInitialMessage,
  onSearchRequest,
}: TopicLayerAttributeSearchQueryFieldsProps) => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();
  const { id: topicId, isPublic = true } = topicLayer;

  const attributeSearch = useAppState((state) => state.attributeSearch);
  const setAttributeSearch = useAppState((state) => state.setAttributeSearch);

  const metadataQuery = useMetadataQuery(topicId);
  const mapLayerSettingQuery = useSubLayersSetting(topicId, topicLayer.url, isPublic);
  const metadata = metadataQuery.data ?? {};
  const layerSetting = mapLayerSettingQuery.data ?? {};

  const layersWithAttributeSearch = Object.values(metadata).filter(
    (l) => l.searchableAttributes.length,
  );

  const sublayerIds = layersWithAttributeSearch
    .map((item) => ({
      label: layerSetting[item.subLayerId]?.name,
      value: item.subLayerId,
    }))
    .sort((a, b) => a.label?.localeCompare(b.label));

  const getAttributeOptions = (sublayerId: number) => {
    const layer = layersWithAttributeSearch.find(
      (item) => item.subLayerId === sublayerId,
    );

    if (!layer?.searchableAttributes) return [];

    return Object.values(layer.searchableAttributes)
      .map(({ anzeigename, bezeichnung, dataTyp }) => ({
        label: anzeigename,
        value: bezeichnung,
        dataTyp,
      }))
      .sort((a, b) => a.label?.localeCompare(b.label));
  };

  const defaultAttributeSearch = {
    sublayerId: sublayerIds[0]?.value,
    attribute: getAttributeOptions(sublayerIds[0]?.value)[0]?.value,
    searchText: '',
    draftQuery: '',
    query: undefined,
    selectedFeatureId: undefined,
  };

  const currentSearch = attributeSearch[topicId] ?? {};
  const {
    sublayerId = defaultAttributeSearch.sublayerId,
    attribute = defaultAttributeSearch.attribute,
    searchText = undefined,
    draftQuery = '',
  } = currentSearch;

  const attributeOptions = getAttributeOptions(sublayerId);

  const updateSearch = (updates: Partial<typeof currentSearch>) =>
    setAttributeSearch(topicId, { ...currentSearch, ...updates });

  const buildQuery = (
    selectedAttributeValue: string,
    searchString: string | undefined,
    dataTyp: any,
  ): string => {
    if (!searchString) return '';

    if (dataTyp === 'Date') return `${selectedAttributeValue} = Date'${searchString}'`;

    if (dataTyp === 'Character string')
      return `UPPER(${selectedAttributeValue}) Like '%${searchString}%'`;

    return `${selectedAttributeValue} = ${Number(searchString)}`;
  };

  useEffect(() => {
    if (!attributeOptions.length) return;

    const selectedAttributeValue = attribute ?? attributeOptions[0]?.value;
    const selectedAttribute = attributeOptions.find(
      (item) => item.value === selectedAttributeValue,
    );

    const query = buildQuery(
      selectedAttributeValue,
      searchText,
      selectedAttribute?.dataTyp,
    );

    updateSearch({ draftQuery: query });
  }, [sublayerId, attribute, searchText]);

  useEffect(() => {
    updateSearch({
      attribute: getAttributeOptions(sublayerId)[0]?.value,
      query: undefined,
      searchText: undefined,
    });
  }, [sublayerId]);

  useEffect(() => {
    updateSearch({ sublayerId, attribute, searchText, draftQuery });
  }, []);

  const searchButtonClicked = () => {
    Tracking.Topics.executeAttributeSearch(topicLayer.id, sublayerId, draftQuery);
    updateSearch({ query: draftQuery, selectedFeatureId: undefined });
    onSearchRequest();
  };

  const resetButtonClicked = () => setAttributeSearch(topicId, defaultAttributeSearch);

  const handleChange = (e: any) => {
    updateSearch({ [e.target.name]: e.target.value });
  };

  const attributeHandleChange = (e: any) =>
    updateSearch({ [e.target.name]: e.target.value, searchText: '' });

  if (metadataQuery.isLoading || mapLayerSettingQuery.isLoading) return null;

  return (
    <div className="tooldialog__wrapper-content">
      <SelectInput
        value={sublayerId}
        label={t('attribute_search_form_sublayer')}
        name="sublayerId"
        onChange={handleChange}
        options={sublayerIds}
        isSearchable={false}
      />
      {attributeOptions && (
        <>
          <SelectInput
            value={attribute}
            label={t('attribute_search_form_attribute')}
            name="attribute"
            onChange={attributeHandleChange}
            options={attributeOptions}
            isSearchable={false}
          />
          <SearchValueInput
            label={t('attribute_search_form_search_value')}
            value={searchText}
            onChange={handleChange}
            attributeName={attribute}
            layercaption={layerSetting[sublayerId]?.name}
            servicename={topicId.split('::')[0]}
            isPublic={isPublic}
            dataTyp={attributeOptions.find((item) => item.value === attribute)?.dataTyp}
          />
        </>
      )}
      <div>
        <Input
          label={t('attribute_search_form_search_query')}
          multiline
          name="draftQuery"
          value={draftQuery}
          disabled={!searchText}
          onChange={handleChange}
        />
        <Link
          additionalClassNames="attribute-search-query-example-link form__actionslink"
          text={t('topic_attribute_search_query_example')}
          url="https://www.ag.ch/de/themen/staat-politik/daten-und-zahlen/geoportal/online-karten?jumpto=NjkxNjAyMi9lNGNiYjQ5My1mNzMxLTQzMjMtOTIwMS1kNTA3ZjBlYWZmNzc"
          title={t('topic_attribute_search_query_example_tooltip')}
          openNewWindow
          type="internal"
        />
      </div>
      <ButtonAV
        onClick={resetButtonClicked}
        disabled={!draftQuery}
        additionalClassNames="button__as-link button__max-width"
        title={t('topic_attribute_search_clear_button')}
      >
        {t('form_reset')}
      </ButtonAV>

      <Button
        onClick={searchButtonClicked}
        disabled={!draftQuery}
        additionalClassNames="button__max-width"
      >
        {t('topic_attribute_search_search_button')}
      </Button>
      {showInitialMessage && (
        <MessageBox type="information" message={t('topic_attribute_search_initialize')} />
      )}
    </div>
  );
};

export default TopicLayerAttributeSearchQueryFields;
